<template>
  <div class="partners">
    <sc-text
      tag="h2"
      class="partners__headline"
      v-if="fields && fields.heading"
      :field="fields.heading"
    />
    <sc-text
      tag="h3"
      class="partners__subheadline"
      v-if="fields && fields.subheading"
      :field="fields.subheading"
    />
    <sc-rich-text
      class="partners__description"
      v-if="fields && fields.description"
      :field="fields.description"
    />
    <div class="partner-list">
      <a v-for="item in partnerPages" :key="item.id" class="partner-list__item" :href="item.url">
        <div class="ribbon">
          <div
            class="ribbon-container"
            v-if="item.fields.accredited?.value === true || item.fields.credited?.value === true"
          >
            <div v-if="item.fields.accredited?.value === true">Accredited</div>
            <div
              v-if="item.fields.accredited?.value === true && item.fields.credited?.value === true"
              class="divider"
            ></div>
            <div v-if="item.fields.credited?.value === true">Credited</div>
          </div>
        </div>
        <div
          class="partner-content"
          :class="{ center: item && !item.fields?.featuredImage?.value?.src }"
        >
          <div class="logo-container" v-if="item && item.fields.featuredImage?.value?.src">
            <img
              :src="item.fields.featuredImage.value.src"
              :alt="item.fields.featuredImage.value.alt"
            />
          </div>
          <div class="title">{{ item.fields.pageTitle.value }}</div>
        </div>
        <div class="cta">See Details</div>
      </a>
    </div>
  </div>
</template>

<script>
import { Text, RichText } from '@sitecore-jss/sitecore-jss-vue';

export default {
  name: 'CIS-Partner-Logo-Wall',
  components: {
    ScText: Text,
    ScRichText: RichText
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    partnerPages() {
      return this?.fields?.partners;
    }
  }
};
</script>
<style lang="scss" scoped>
.partner {
  &-list {
    display: grid;
    gap: 4rem 2rem;
    grid-template-columns: repeat(2, 1fr);
    margin-top: 4rem;

    @include tablet-up {
      gap: 4rem 2rem;
      grid-template-columns: repeat(3, 1fr);
    }

    @include desktop-up {
      gap: 4rem;
      grid-template-columns: repeat(2, 1fr);
    }

    @include large-desktop-up {
      gap: 4rem;
      grid-template-columns: repeat(3, 1fr);
    }

    &__item {
      display: flex;
      flex-direction: column;
      padding: 0.8rem 0.8rem 1rem 0.8rem;
      align-items: center;
      justify-content: center;
      border-top: 3px solid $orange;
      border-right: 2px solid $divider;
      border-bottom: 2px solid $divider;
      border-left: 2px solid $divider;
      min-height: 21rem;

      @include tablet-up {
        padding: 1.6rem;
      }

      @include large-desktop-up {
        min-height: 35rem;
      }

      .ribbon {
        min-height: 2.5rem;

        &-container {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          text-transform: uppercase;

          div {
            @include font-styles('Arial Narrow', 1.2rem, 600, normal, normal, 0, $gray);

            @include tablet-up {
              font-size: 1.6rem;
              line-height: 1.44rem;
            }
          }

          .divider {
            width: 0.15rem;
            height: 1.2rem;
            margin: 0.05rem 0.5rem 0 0.5rem;
            background: rgba(48, 62, 72, 0.5);
            position: relative;
            border: none;
          }

          &:before {
            content: '';
            position: relative;
            background-image: url(~@/assets/images/icon-ribbon.svg);
            background-repeat: no-repeat;
            background-size: contain;
            margin-right: 0.5rem;
            width: 1.6rem;
            min-width: 1.6rem;
            height: 2.08rem;
            min-height: 2.08rem;
          }
        }
      }

      .partner-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;

        &.center {
          height: 100%;

          .title {
            margin-bottom: 0;
          }
        }

        .logo-container {
          height: 7.2rem;
          max-height: 7.2rem;
          width: auto;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: hidden;
          margin-bottom: 0.8rem;
          padding: 0.8rem;

          @include tablet-up {
            height: 12rem;
            max-height: 12rem;
            margin-bottom: 2rem;
          }

          img {
            max-height: 100%;
            max-width: 100%;
          }
        }
      }

      .title {
        color: $gray;
        font-weight: 300;
        font-size: 2rem;
        line-height: 2.6rem;
        margin-bottom: 0.8rem;
        text-align: center;

        @include desktop-up {
          font-size: 2.8rem;
          line-height: 3.5rem;
          margin-bottom: 2rem;
        }
      }

      .cta {
        display: inline-flex;
        text-transform: uppercase;
        justify-content: flex-start;
        align-items: center;
        @include font-styles('Arial Narrow', 1.8rem, 700, normal, 2.2rem, 0, $blue);
        margin-top: auto;

        &:after {
          content: '';
          position: relative;
          background-image: url(~@/assets/images/icon-arrow-right-blue-sm.svg);
          background-repeat: no-repeat;
          background-size: contain;
          margin-left: 1rem;
          width: 1.9rem;
          min-width: 1.9rem;
          height: 1.5rem;
          min-height: 1.5rem;
          left: 0;
          transition: left 0.25s;
        }
      }

      &:hover {
        .cta:after {
          left: 1rem;
        }
      }
    }

    .nofeaturedImage {
      clear: both;
      width: 100%;

      @include tablet-up {
        display: flex;
        flex-wrap: wrap;
      }

      > div {
        margin-bottom: 1rem;

        @include tablet-up {
          flex-basis: 50%;
        }

        a,
        span {
          display: inline-block;
          padding-right: 2rem;
        }
      }
    }
  }
}
</style>
